import React, { Suspense } from 'react'

import { Button, Modal, ModalInjectedProps, Skeleton } from '@sketch/components'
import { useArchiveStorageItemsMutation } from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import {
  removeFromPaginated,
  removeFromStorageItemsList,
} from '@sketch/modules-common'
import { StorageItemIdentity } from '../../types'
import { formatStorageItemsCountMessage, useGetStorageItem } from '../../hooks'

interface SafeguardedStorageItemNameProps {
  item: StorageItemIdentity
}

const SafeguardedStorageItemName = ({
  item,
}: SafeguardedStorageItemNameProps) => {
  const storageItem = useGetStorageItem(item)

  return <>“{storageItem?.name}”</>
}

interface ArchiveStorageItemsModalBody {
  items: StorageItemIdentity[]
}

const ArchiveStorageItemsModalBody = ({
  items,
}: ArchiveStorageItemsModalBody) => {
  const { message } = formatStorageItemsCountMessage(items)

  return (
    <>
      Move <strong>{message}</strong> to Archive? <br />
      You can still view and edit archived documents.
    </>
  )
}

interface ArchiveStorageItemModalBodyProps {
  item: StorageItemIdentity
}

const ArchiveStorageItemModalBody = ({
  item,
}: ArchiveStorageItemModalBodyProps) => {
  const name = (
    <Suspense fallback={<Skeleton width="100px" />}>
      <strong>
        <SafeguardedStorageItemName item={item} />
      </strong>
    </Suspense>
  )

  const typeLower = item.__typename === 'Project' ? 'folder' : 'document'

  return (
    <>
      {name} {typeLower}
      {item.__typename === 'Project' ? ' and all its documents ' : ' '}
      will move to the Archive. You can still view and edit archived documents.
    </>
  )
}

interface ArchiveStorageItemsModalProps extends ModalInjectedProps {
  items: StorageItemIdentity[]
  onArchive?: () => void
}

export const ArchiveStorageItemsModal = (
  props: ArchiveStorageItemsModalProps
) => {
  const { hideModal, items, onArchive } = props
  const { showToast } = useToast()

  const [archiveItem, { loading }] = useArchiveStorageItemsMutation({
    variables: {
      storageItems: items.map(item => ({
        identifier: item.identifier,
        type: item.__typename === 'Share' ? 'SHARE' : 'PROJECT',
      })),
    },
    onError: 'show-toast',
    onCompleted: ({ archiveStorageItems }) => {
      const { storageItems } = archiveStorageItems
      if (storageItems.length > 1) {
        showToast('Selection Archived')
      } else {
        const [item] = storageItems
        showToast(`“${item.name}” Archived`)
      }

      hideModal()
      onArchive?.()
    },
    update: (cache, { data }) => {
      const items = data?.archiveStorageItems.storageItems || []

      items.forEach(item => {
        removeFromStorageItemsList({ cache, target: item })

        const listToRemoveFrom = ['.projects', '.shares']
        removeFromPaginated(cache, item, key =>
          listToRemoveFrom.some(k => key.includes(k))
        )
      })
    },
  })

  const hasMultipleItems = items.length > 1
  const [item] = items
  const typeTerm = hasMultipleItems
    ? 'Selection'
    : item.__typename === 'Project'
      ? 'Folder'
      : 'Document'

  let content = null

  if (!hasMultipleItems) {
    content = <ArchiveStorageItemModalBody item={item} />
  } else {
    content = <ArchiveStorageItemsModalBody items={items} />
  }

  return (
    <Modal onCancel={hideModal}>
      <Modal.Header>Archive {typeTerm}?</Modal.Header>
      <Modal.Body>{content}</Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal}>Cancel</Button>
        <Button
          variant="primary"
          onClick={() => archiveItem()}
          loading={loading}
        >
          Archive {typeTerm}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
